import {
  collection,
  doc,
  serverTimestamp,
  Timestamp,
  writeBatch,
  setDoc,
} from "firebase/firestore"
import { auth, firestore } from "../firebase"
import { ensureUserDoc } from "./userDoc"
import {
  DEFAULT_PROMPT_ALT,
  LANDING_MARBLE_STYLE_URL,
  LANDING_MODEL_STYLE_URL,
  MARBLE_PROMPT,
  MARBLE_STONE_PROMPT,
  MODEL_SELECT_PROMPT,
} from "../constants/tryonPrompts"

export function getLandingTryonJobs() {
  return [
    { prompt: MARBLE_PROMPT, styleImageUrl: null, aspectRatio: "2:3" },
    { prompt: DEFAULT_PROMPT_ALT, styleImageUrl: null, aspectRatio: "2:3" },
    {
      prompt: MODEL_SELECT_PROMPT,
      styleImageUrl: LANDING_MODEL_STYLE_URL,
      aspectRatio: "9:16",
    },
    {
      prompt: MARBLE_STONE_PROMPT,
      styleImageUrl: LANDING_MARBLE_STYLE_URL,
      aspectRatio: "9:16",
    },
  ]
}

export const LANDING_GENERATE_URL =
  "https://us-central1-jewlai2.cloudfunctions.net/landing_generate"
export const LANDING_STATUS_URL =
  "https://us-central1-jewlai2.cloudfunctions.net/landing_status"

export function isLandingTrialId(id) {
  return (
    typeof id === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
  )
}

export function emptyLandingSlots() {
  const startedAt = Date.now()
  return getLandingTryonJobs().map((job, i) => ({
    id: `slot-${i}`,
    url: null,
    status: "pending",
    error: null,
    prompt: job.prompt,
    createdAt: startedAt,
  }))
}

export function promptForLandingIndex(index) {
  return getLandingTryonJobs()[index]?.prompt || null
}

export function landingCreatedAtMs(value) {
  if (!value) return 0
  if (value instanceof Date) return value.getTime()
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value.toMillis === "function") return value.toMillis()
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function galleryItemsFromLandingHandoff(handoff) {
  const results = Array.isArray(handoff?.results) ? handoff.results : []
  const hasTrial = isLandingTrialId(handoff?.trialId)
  if (results.length === 0 && !hasTrial) return []
  const slots = results.length > 0 ? results : emptyLandingSlots()
  const startedAt = landingCreatedAtMs(handoff?.startedAt)
  return slots.map((img, index) => {
    const url = img.url || null
    const failed = img.status === "failed"
    const createdMs = landingCreatedAtMs(img.createdAt) || startedAt
    return {
      id: `landing-${img.id || index}`,
      baseImageId: `landing-${img.id || index}`,
      imageUrl: url,
      isLoading: !url && !failed,
      isFailed: failed,
      error: img.error || null,
      createdAt: createdMs ? new Date(createdMs) : new Date(0),
      landingOrder: index,
      prompt: img.prompt || promptForLandingIndex(index),
    }
  })
}

const LANDING_RESULTS_KEY = "jewlai_landing_results"
const TRIAL_STORAGE_KEY = "jewlai_landing_trial"

export function readStoredLandingTrial() {
  try {
    const raw = localStorage.getItem(TRIAL_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && (typeof parsed.trialId === "string" || parsed.used)) return parsed
  } catch (_) {
    // ignore
  }
  return null
}

export async function attachLandingTrialToUser(userId, handoff) {
  if (!userId) return false
  const trialId = handoff?.trialId || null
  if (!isLandingTrialId(trialId)) return false

  const urls = Array.isArray(handoff?.results)
    ? handoff.results.map((img) => img?.url).filter(Boolean)
    : []

  const payload = {
    fromLandingTrial: true,
    landingTrialId: trialId,
    landingUserId: `landing_${trialId}`,
    landingInputImageUrl: handoff?.imageUrl || null,
  }
  if (urls.length > 0) {
    payload.landingImageUrls = urls
    payload.landingImportedAt = serverTimestamp()
  }

  // Create the full user doc first; a bare doc from this merge would lack balance/plan/createdAt.
  if (auth.currentUser?.uid === userId) {
    await ensureUserDoc(auth.currentUser, { touchLogin: false }).catch((err) => {
      console.error("user doc setup failed", err)
    })
  }
  await setDoc(doc(firestore, "users", userId), payload, { merge: true })
  return true
}

export async function attachCurrentLandingTrialToUser(userId) {
  const handoff = readLandingResultsHandoff()
  const stored = readStoredLandingTrial()
  const trialId = handoff?.trialId || stored?.trialId || null
  if (!isLandingTrialId(trialId)) return false
  return attachLandingTrialToUser(userId, {
    trialId,
    imageUrl: handoff?.imageUrl || stored?.imageUrl || null,
    results: Array.isArray(handoff?.results) ? handoff.results : [],
  })
}

export function readLandingResultsHandoff() {
  try {
    const raw = sessionStorage.getItem(LANDING_RESULTS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || (!parsed.trialId && (!Array.isArray(parsed.results) || parsed.results.length === 0))) {
      return null
    }
    return parsed
  } catch (_) {
    return null
  }
}

export function storeLandingResultsHandoff(handoff) {
  try {
    sessionStorage.setItem(LANDING_RESULTS_KEY, JSON.stringify(handoff))
  } catch (_) {
    // ignore quota
  }
}

export function clearLandingResultsHandoff() {
  try {
    sessionStorage.removeItem(LANDING_RESULTS_KEY)
  } catch (_) {
    // ignore
  }
}

function importFlagKey(userId, trialId) {
  return `jewlai_landing_imported:${userId}:${trialId || "na"}`
}

export async function importLandingResultsToUser(userId, handoff) {
  if (!userId || !handoff?.results?.length) return false
  const succeeded = handoff.results.filter((img) => img?.url)
  if (succeeded.length === 0) {
    await attachLandingTrialToUser(userId, handoff).catch((err) => {
      console.error("landing user link failed", err)
    })
    return false
  }

  await attachLandingTrialToUser(userId, handoff).catch((err) => {
    console.error("landing user link failed", err)
  })

  const flag = importFlagKey(userId, handoff.trialId)
  try {
    if (sessionStorage.getItem(flag) === "1") return false
  } catch (_) {
    // continue even if storage is unavailable
  }

  const batch = writeBatch(firestore)
  const imageCollectionRef = collection(firestore, "users", userId, "image")
  succeeded.forEach((img, index) => {
    const imageRef = doc(imageCollectionRef)
    batch.set(imageRef, {
      imageUrl: handoff.imageUrl || img.url,
      processedImageUrlR2: img.url,
      generationStatus: "succeeded",
      skipGeneration: true,
      isLandingImport: true,
      isLandingTrial: true,
      sourceTrialId: handoff.trialId || null,
      sourceImageId: img.id || null,
      createdAt: (() => {
        const ms = landingCreatedAtMs(img.createdAt) || landingCreatedAtMs(handoff.startedAt)
        return ms ? Timestamp.fromMillis(ms) : serverTimestamp()
      })(),
      processedAt: serverTimestamp(),
      aspect_ratio: getLandingTryonJobs()[index]?.aspectRatio || "2:3",
      size: "2K",
      model: "muse",
      output_format: "jpeg",
      prompt: img.prompt || promptForLandingIndex(index),
      order: index,
    })
  })
  await batch.commit()
  try {
    sessionStorage.setItem(flag, "1")
  } catch (_) {
    // ignore quota
  }
  return true
}

export async function createLandingTryonJobs(userId, imageUrl) {
  if (!userId) throw new Error("Please sign in to generate.")
  if (!imageUrl) throw new Error("Please upload a product photo first.")

  const jobs = getLandingTryonJobs()
  const batch = writeBatch(firestore)
  const imageCollectionRef = collection(firestore, "users", userId, "image")

  jobs.forEach((job, index) => {
    const imageRef = doc(imageCollectionRef)
    batch.set(imageRef, {
      imageUrl,
      styleImageUrl: job.styleImageUrl,
      prompt: job.prompt,
      createdAt: serverTimestamp(),
      processedAt: null,
      aspect_ratio: job.aspectRatio || "2:3",
      size: "2K",
      model: "muse",
      output_format: "jpeg",
      isLandingTrial: true,
      order: index,
    })
  })

  await batch.commit()
}
